import { MonitoringEvent } from "@/types/monitoring";
import { Card, CardContent } from "@/components/ui/card";
import { 
  TabletSmartphone, 
  Clock, 
  AlertTriangle,
  Eye,
  FileText,
  MapPin,
  Clipboard,
  Camera
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface TestActivityTimelineProps {
  events: MonitoringEvent[];
}

export function TestActivityTimeline({ events }: TestActivityTimelineProps) {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No monitoring events recorded for this test
      </div>
    );
  }

  // Sort events by timestamp and filter out webcam capture events
  const sortedEvents = [...events]
    .filter(event => event.event_type !== 'webcam_capture')
    .sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  
  // Get icon for event type
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'tab_switch':
      case 'window_blur':
        return <TabletSmartphone className="h-4 w-4" />;
      case 'timing_anomaly':
        return <Clock className="h-4 w-4" />;
      case 'ai_content':
        return <Eye className="h-4 w-4" />;
      case 'content_similarity':
        return <FileText className="h-4 w-4" />;
      case 'device_location':
        return <MapPin className="h-4 w-4" />;
      case 'clipboard_action':
        return <Clipboard className="h-4 w-4" />;
      case 'webcam_capture':
        return <Camera className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };
  
  // Format event timestamp
  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'HH:mm:ss');
  };
  
  // Get event severity class
  const getSeverityClass = (severity: number) => {
    if (severity > 2) return "text-red-500 dark:text-red-400";
    if (severity > 1) return "text-amber-500 dark:text-amber-400";
    return "text-green-500 dark:text-green-400";
  };
  
  // Format event details with improved descriptions
  const formatEventDetails = (event: MonitoringEvent) => {
    const data = event.event_data || {};
    const time = formatTimestamp(event.created_at);
    
    switch (event.event_type) {
      case 'tab_switch':
        return `Tab Focus ${data.action === 'leave' ? 'Lost' : 'Regained'} at ${time}. ${data.duration_ms ? `Duration: ${(data.duration_ms / 1000).toFixed(1)}s.` : ''}`;
      case 'window_blur':
        return `Window ${data.action === 'blur' ? 'Lost Focus' : 'Regained Focus'} at ${time}. ${data.duration_ms ? `Duration: ${(data.duration_ms / 1000).toFixed(1)}s.` : ''}`;
      case 'timing_anomaly':
        return `Timing Anomaly at ${time}. ${data.duration ? `Duration: ${data.duration}s.` : ''} ${data.description || ''}`;
      case 'ai_content':
        return `AI-generated content detected at ${time}. Confidence: ${data.confidence_score ? `${(data.confidence_score * 100).toFixed(1)}%` : 'Unknown'}`;
      case 'content_similarity':
        return `Content similarity detected at ${time}. Similarity score: ${data.similarity_score ? `${(data.similarity_score * 100).toFixed(1)}%` : 'Unknown'}`;
      case 'device_location':
        return `Location change detected at ${time}. ${data.location ? `New location: ${data.location}` : ''}`;
      case 'clipboard_action':
        return `Clipboard ${data.action} detected at ${time}. Target: ${data.targetTagName || 'Unknown element'}`;
      case 'webcam_capture':
        return `Webcam ${data.action || 'event'} at ${time}${data.filename ? `. Snapshot: ${data.filename}` : ''}`;
      default:
        // Fix: Cast event.event_type to string before calling replace
        const eventTypeString = String(event.event_type);
        return `Event: ${eventTypeString.replace(/_/g, ' ')} at ${time}`;
    }
  };

  // Get simplified key/value pairs for additional details
  const getRelevantDetails = (event: MonitoringEvent) => {
    const data = event.event_data || {};
    const ignoredKeys = ['pattern', 'duration', 'duration_ms', 'action', 'key', 'timeGap', 'distance', 'description', 'confidence_score', 'similarity_score', 'location'];
    
    return Object.entries(data)
      .filter(([key]) => !ignoredKeys.includes(key))
      .map(([key, value]) => ({
        key: key.replace(/_/g, ' '),
        value: typeof value === 'object' ? JSON.stringify(value) : String(value)
      }));
  };

  return (
    <div className="relative p-1">
      {/* Timeline track */}
      <div className="absolute left-5 top-8 bottom-8 w-px bg-border/40"></div>
      
      <div className="space-y-4">
        {sortedEvents.map((event, index) => (
          <div key={index} className="relative ml-10 pb-4">
            {/* Timeline node */}
            <div className={cn(
              "absolute -left-5 mt-1.5 h-4 w-4 rounded-full border-2 border-background flex items-center justify-center",
              getSeverityClass(event.severity)
            )}>
              {getEventIcon(event.event_type)}
            </div>
            
            <Card className="ml-2 shadow-sm">
              <CardContent className="p-3">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-xs font-semibold",
                      getSeverityClass(event.severity)
                    )}>
                      {String(event.event_type).replace(/_/g, ' ')}
                    </span>
                    <Badge variant={event.severity > 2 ? "destructive" : (event.severity > 1 ? "secondary" : "outline")}>
                      {event.severity > 2 ? "High" : (event.severity > 1 ? "Medium" : "Low")}
                    </Badge>
                  </div>
                  
                  {event.question_id && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Question {event.event_data?.question_number || "N/A"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(event.created_at)}
                      </span>
                    </div>
                  )}
                  
                  {!event.question_id && (
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(event.created_at)}
                    </span>
                  )}
                </div>
                
                <p className="text-sm mt-2">
                  {formatEventDetails(event)}
                </p>
                
                {/* Only show additional details if there are relevant ones */}
                {getRelevantDetails(event).length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                      Additional details
                    </summary>
                    <div className="mt-2 pt-2 border-t border-border/20 grid grid-cols-1 gap-1">
                      {getRelevantDetails(event).map((detail, i) => (
                        <div key={i} className="flex items-start">
                          <span className="font-medium capitalize mr-2">{detail.key}:</span>
                          <span className="text-muted-foreground">{detail.value}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}
